import React, { useEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, Link2, BookOpen } from 'lucide-react';
import { api } from '../api';
import { BRAND } from '../kit/tokens';
import { Dialog, DialogPortal, DialogOverlay } from '../../components/ui/dialog';

// Simple inline link version — same popover but rendered as underlined text link
export function CitationLink({ topic, agency, label }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative">
      <button
        onClick={() => setOpen(true)}
        data-testid={`citation-link-${topic}`}
        className="text-[11px] font-semibold underline decoration-dotted hover:text-brand-navy text-brand-accent"
      >
        <BookOpen size={11} className="inline mr-1" />
        {label}
      </button>
      {open && <CitationPopover topic={topic} agency={agency} onClose={() => setOpen(false)} />}
    </span>
  );
}

// Fetches /api/guidelines/citations/{topic} and shows a floating card of sources.
export function CitationPopover({ topic, agency, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    api.get(`/guidelines/citations/${topic}?agency=${agency || ''}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ citations: [] }))
      .finally(() => setBusy(false));
  }, [topic, agency]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPortal>
        <DialogOverlay className="z-40 bg-transparent" />
        <DialogPrimitive.Content
          className="fixed z-50 max-w-lg w-[90vw] bg-white shadow-2xl rounded-xl border border-slate-200 p-4 left-1/2 -translate-x-1/2"
          style={{ top: '20%' }}
          data-testid={`citation-popover-${topic}`}
        >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-brand-accent">Guideline Citation</div>
            <DialogPrimitive.Title className="font-serif text-base font-semibold text-brand-navy">
              {topic.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </DialogPrimitive.Title>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        {busy && <div className="text-xs text-slate-500">Loading…</div>}
        {!busy && (!data || !data.citations || data.citations.length === 0) && (
          <div className="text-xs text-slate-500">No citation found for this topic + agency.</div>
        )}
        {!busy && data?.citations?.length > 0 && (
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {data.citations.map((c) => (
              <div key={c.id} className="border-l-2 border-brand-accent pl-3 py-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: '#EEF2FF', color: BRAND.navy }}>
                    {c.agency.toUpperCase()} §{c.section_id}
                  </span>
                  <a href={c.source_url} target="_blank" rel="noopener noreferrer"
                     className="text-[10px] text-slate-500 hover:text-brand-accent flex items-center gap-1">
                    Source <Link2 size={9} />
                  </a>
                </div>
                <div className="text-sm font-semibold text-slate-800 mb-1">{c.section_title}</div>
                <div className="text-xs text-slate-600 leading-relaxed">{c.content}</div>
              </div>
            ))}
          </div>
        )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
